import { DynamicForm, useApiService, validators, withFormService } from "@art/shared-ui";
import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

function AddGroups({ data, onSave, onInit }: any) {
    const apiService = useApiService();
    const formBuilder = (data: any) => {
        return [
            {
                type: "row",
                children: [
                    {
                        type: "col",
                        classNames: ["col-12"],
                        children: [
                            {
                                id: "group_name",
                                label: "Group Name",
                                type: "text",
                                dataQa: 'app-group-name',
                                placeholder: "Enter your group name",
                                required: true,
                                value: data?.group_name || "",
                                validate: validators((value) => {
                                    if (!value || value.trim() === "") {
                                        return "Group Name is required.";
                                    }
                                    if (value.trim().length < 3) {
                                        return "Group Name must be at least 3 characters long.";
                                    }
                                    return null;
                                }),
                            },
                        ],
                    },
                ],
            },
            {
                type: "row",
                children: [
                    {
                        type: "col",
                        classNames: ["col-12"],
                        children: [
                            {
                                id: "assign_user",
                                label: "Assign User",
                                type: "custom",
                                required: true,
                                dataQa: 'users',
                                value: data?.users?.map((users: any) => users.id) || [],
                                validate: validators((value) => {
                                    if (!value || !Array.isArray(value) || value.length === 0) {
                                        return "Users are required.";
                                    }
                                    return null;
                                }),
                                builder: ({ value, onChange, error }: any) => {
                                    const [options, setOptions] = useState<{ label: string; value: string }[]>(
                                        () => data?.users?.map((user: any) => ({ label: user.username, value: user.id })) || []
                                    );
                                    const [inputValue, setInputValue] = useState("");
                                    const [loading, setLoading] = useState(false);
                                    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

                                    const fetchUsers = (search: string) => {
                                        setLoading(true);
                                        apiService
                                            .get(`/art-users?page=1&limit=20${search.trim() ? `&search_text=${encodeURIComponent(search.trim())}` : ""}`)
                                            .then((response: any) => {
                                                const users = response.data?.data?.users || [];
                                                const fetched = users.map((user: any) => ({
                                                    label: user.username,
                                                    value: user.id,
                                                }));

                                                setOptions((prev) => {
                                                    const selected = prev.filter((opt) => value?.includes(opt.value));
                                                    const merged = new Map(selected.map((opt) => [opt.value, opt]));
                                                    fetched.forEach((opt: any) => merged.set(opt.value, opt));
                                                    return Array.from(merged.values());
                                                });
                                            })
                                            .catch((error) => {
                                                console.error("Error fetching users:", error);
                                            })
                                            .finally(() => setLoading(false));
                                    };

                                    useEffect(() => {
                                        fetchUsers("");
                                    }, []);

                                    useEffect(() => {
                                        if (debounceRef.current) clearTimeout(debounceRef.current);
                                        debounceRef.current = setTimeout(() => fetchUsers(inputValue), 300);
                                        return () => {
                                            if (debounceRef.current) clearTimeout(debounceRef.current);
                                        };
                                    }, [inputValue]);

                                    return (
                                        <Autocomplete
                                            multiple
                                            limitTags={3}
                                            options={options}
                                            loading={loading}
                                            filterOptions={(opts) => opts}
                                            getOptionLabel={(option) => option.label}
                                            isOptionEqualToValue={(option, val) => option.value === val.value}
                                            value={options.filter((opt) => value?.includes(opt.value))}
                                            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                                            onChange={(_, newValue) => {
                                                const selectedValues = newValue.map((opt) => opt.value);
                                                onChange({ target: { value: selectedValues } });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Assign User *"
                                                    placeholder="Search users by username"
                                                    variant="outlined"
                                                    error={!!error}
                                                    helperText={error || (value?.length > 0 ? `${value.length} user(s) selected` : undefined)}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {loading && <CircularProgress color="inherit" size={16} />}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                            data-qa='users'
                                            size="small"
                                            sx={{
                                                '& .MuiChip-label': {
                                                    color: 'white',
                                                    padding: '16px',
                                                    fontSize: '14px'
                                                },
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'white !important'
                                                }
                                            }}


                                        />
                                    );
                                },
                            }
                        ],
                    },
                ],
            },
        ];
    };

    return (
        <DynamicForm
            formBuilder={formBuilder}
            onInit={onInit}
            onSubmit={!data ? apiService.saveData("/groups", "post", {
                onApiCall: (data: any) => ({
                    group_name: data.group_name,
                    users: data.assign_user.map((id: string) => ({ id })),
                }),
                onSuccess: (_res: any) => {
                    onSave();
                },
                onError: (err: any) => {
                    console.error("Error:", err);
                },
            }) : apiService.saveData(`/groups/${data?.id}`, "put", {
                onApiCall: (data: any) => ({
                    group_name: data.group_name,
                    users: data.assign_user.map((id: string) => ({ id })),
                }),
                onSuccess: (_res: any) => {


                    onSave();
                },
                onError: (err: any) => {
                    console.error("Error:", err);
                },
            })}
        />
    );
}

export default withFormService(AddGroups);
